import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { FormattedMessage } from "react-intl";
import { useParams } from "react-router";
import Form, { SIZE, TYPE } from "components/Form";
import { EmailChangeModal } from "components/Profile";
import { useAuth, useProfile } from "hooks";
import { ARCHETYPES, PARTNER_CHANNELS, READ_ONLY_CHANNELS } from "constants/index";
import { GET_ACQUISITION_SOURCE, GET_PARTNERS } from "graphql/queries";
import { colors, fontSize, margins } from "style";
import { Link } from "components/Links";

/**
 * Profile Setting Form
 */
const ProfileSettingForm = () => {
  const { id } = useParams();
  const { data, loading, updateProfile } = useProfile({ profileId: id });
  const { isMember } = useAuth();
  const [channel, setChannel] = useState();
  const [profileSource, setProfileSource] = useState();
  const [profile, setProfileData] = useState();
  const [allowedSkip, setAllowedSkip] = useState(true);
  const [defaultSourceName, setDefaultSourceName] = useState();
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);

  const handleChannelChange = (value) => {
    setAllowedSkip(false);
    setChannel(value);
    setProfileSource(null);
  };

  const handleChangeSource = (source) => {
    setAllowedSkip(true);
    setProfileSource(source);
  };

  useEffect(() => {
    setProfileData(data?.profile);
    setProfileSource(data?.profile?.source?.id);
    setDefaultSourceName(data?.profile?.source?.name);
  }, [data?.profile]);

  const handleSubmit = (formData) => {
    const { email, missionEmail, partnerId, channel, archetype, deelContractId } = formData;

    updateProfile(id, {
      user: {
        email,
        missionEmail,
      },
      channel,
      source: profileSource,
      archetype,
      deelContractId,
      partnerId,
    });
  };

  if (loading || !profile) return null;

  const initialValues = {
    email: profile?.email,
    missionEmail: profile?.missionEmail,
    partnerId: profile?.partner?.id,
    channel: profile?.channel,
    source: profileSource,
    archetype: profile?.archetype,
    deelContractId: profile?.deelContractId,
  };

  const args = {
    setShowEmailChangeModal,
    unconfirmedEmail: profile?.user?.unconfirmedEmail,
    ...(!isMember && {
      channel,
      defaultSourceName,
      handleChannelChange,
      handleChangeSource,
      allowedSkip,
    }),
  };

  return (
    <>
      <Form
        data={(isMember ? MEMBER_FORM_DATA : FORM_DATA)(args)}
        saveButton={{
          show: !isMember,
          labelId: "Profile.Setting.FormSaveButton",
          disabled: loading,
        }}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        loading={loading}
        marginSize={margins.none}
        numColumns={4}
        noPadding
      />
      <EmailChangeModal
        updateId={profile?.id}
        show={showEmailChangeModal}
        onClose={() => setShowEmailChangeModal(false)}
        parentObjectKey="user"
        loading={loading}
      />
    </>
  );
};

const MEMBER_FORM_DATA = ({ setShowEmailChangeModal, unconfirmedEmail }) => [
  {
    style: css`
      width: 100%;
    `,
    items: [
      {
        label: "Account Settings",
        type: TYPE.h4,
        size: SIZE.xlarge,
      },
      EMAIL_FIELD(setShowEmailChangeModal),
      EMAIL_DESCRIPTION_FIELD(unconfirmedEmail),
    ].filter(Boolean),
  },
];

const styles = {
  email_description: css`
    font-size: ${fontSize.xsmall};
    font-style: italic;
  `,
  change_button: css`
    position: absolute;
    right: 1px;
    top: 1px;
    bottom: 1px;
    min-height: 0;
    padding: 0 1rem;
    height: calc(4rem - 2px);
    border-top-right-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    background: ${colors.purpleRainBase};
    color: white;
    border: none;
    cursor: pointer;
    transition: all 0.2s;
    font-size: ${fontSize.xxxsmall};
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
};

const FORM_DATA = ({
  channel,
  defaultSourceName,
  handleChannelChange,
  handleChangeSource,
  allowedSkip,
  setShowEmailChangeModal,
  unconfirmedEmail,
}) => [
  {
    style: css`
      width: 100%;
    `,
    items: [
      {
        label: "Email",
        type: TYPE.h4,
        size: SIZE.xlarge,
      },
      EMAIL_FIELD(setShowEmailChangeModal),
      {
        label: "Mission Email Address",
        type: TYPE.input,
        size: SIZE.medium,
        properties: {
          name: "missionEmail",
          type: "email",
        },
      },
      EMAIL_DESCRIPTION_FIELD(unconfirmedEmail),
      {
        label: "Partner",
        type: TYPE.h4,
        size: SIZE.xlarge,
      },
      {
        type: TYPE.select,
        size: SIZE.small,
        properties: {
          name: "partnerId",
          queryName: GET_PARTNERS,
          queryKeyName: "partners",
          queryFieldName: "partners",
          labelKey: "formattedName",
          valueKey: "id",
          isClearable: true,
        },
      },
      {
        label: "Acquisition",
        type: TYPE.h4,
        size: SIZE.xlarge,
      },
      {
        label: "Channel",
        type: TYPE.select,
        size: SIZE.small,
        properties: {
          name: "channel",
          required: false,
          reloadData: (channel) => handleChannelChange(channel),
          options: PARTNER_CHANNELS,
          labelKey: "label",
          valueKey: "value",
        },
      },
      {
        label: "Source",
        type: TYPE.select,
        size: SIZE.small,
        disabled: READ_ONLY_CHANNELS.includes(channel) || !channel,
        properties: {
          name: "source",
          queryVariables: { filters: { channel: channel } },
          queryName: GET_ACQUISITION_SOURCE,
          queryKeyName: "acquisitionSource",
          queryFieldName: "acquisitionSource",
          querySearchFieldName: "search",
          fetchPolicy: "network-only",
          nextFetchPolicy: "network-only",
          defaultSearch: defaultSourceName,
          labelKey: "name",
          valueKey: "id",
          isSearchable: true,
          allowedSkip: allowedSkip,
          reloadData: (source) => handleChangeSource(source),
        },
      },
      {
        label: "Role & Contract ID",
        type: TYPE.h4,
        size: SIZE.xlarge,
      },
      {
        label: "Role",
        type: TYPE.select,
        size: SIZE.small,
        properties: {
          name: "archetype",
          options: Object.values(ARCHETYPES),
          labelKey: "name",
          valueKey: "id",
        },
      },
      {
        label: "Deel Contract ID",
        type: TYPE.input,
        size: SIZE.small,
        properties: {
          name: "deelContractId",
          type: "text",
        },
      },
    ].filter(Boolean),
  },
];

const EMAIL_FIELD = (setShowEmailChangeModal) => ({
  label: "Email Address",
  type: TYPE.input,
  size: SIZE.medium,
  properties: {
    required: true,
    name: "email",
    type: "email",
    readOnly: true,
    withButton: true,
    buttonComponent: EmailFieldButtonComponent,
    buttonComponentProps: { onClick: () => setShowEmailChangeModal(true) },
  },
});

const EMAIL_DESCRIPTION_FIELD = (unconfirmedEmail) => ({
  type: TYPE.p,
  description: unconfirmedEmail ? (
    <span css={styles.email_description}>
      <FormattedMessage
        id="Profile.Setting.Form.EmailFieldDescription"
        values={{
          email: <Link to={`mailto:${unconfirmedEmail}`}>{unconfirmedEmail}</Link>,
        }}
      />
    </span>
  ) : null,
  hideField: !unconfirmedEmail,
});

const EmailFieldButtonComponent = ({ onClick }) => (
  <div css={styles.change_button} onClick={onClick}>
    <FormattedMessage id="Global.Change" />
  </div>
);

EmailFieldButtonComponent.propTypes = {
  onClick: PropTypes.func.isRequired,
};

export default ProfileSettingForm;
